class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.initReBuyButtonMonitoring();
  }

  initReBuyButtonMonitoring() {
    if (!this.isConnected) return;

    if (!this.rebuyObserver) {
      this.rebuyObserver = new MutationObserver((mutations) => {
        let hasNewButtons = false;
        
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const rebuyButtons = node.querySelectorAll('.rebuy-button:not([data-rebuy-monitored])');
                if (rebuyButtons.length) {
                  hasNewButtons = true;
                  this.attachRebuyButtonListeners(rebuyButtons);
                }
              }
            });
          }
        });
        
        if (hasNewButtons) {
          this.initExistingReBuyButtons();
        }
      });
    }

    this.rebuyObserver.observe(this, {
      childList: true,
      subtree: true
    });

    this.initExistingReBuyButtons();
  }

  initExistingReBuyButtons() {
    const existingButtons = this.querySelectorAll('.rebuy-button:not([data-rebuy-monitored])');
    if (existingButtons.length > 0) {
      this.attachRebuyButtonListeners(existingButtons);
    }
  }
  
  attachRebuyButtonListeners(buttons) {
    buttons.forEach(button => {
      button.setAttribute('data-rebuy-monitored', 'true');
      button.addEventListener('click', this.handleRebuyButtonClick.bind(this));
    });
  }
  
  handleRebuyButtonClick() {
    clearTimeout(this.rebuyRefreshTimeout);
    this.rebuyRefreshTimeout = setTimeout(() => {
      this.refreshCartContents();
    }, 750);
  }
  
  refreshCartContents() {
    Promise.all([
      fetch(`${window.routes.cart_url}?section_id=cart-drawer`).then(response => response.text()),
      fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`).then(response => response.text())
    ])
    .then(([drawerText, bubbleText]) => {
      const drawerHtml = new DOMParser().parseFromString(drawerText, 'text/html');
      const bubbleHtml = new DOMParser().parseFromString(bubbleText, 'text/html');
      
      const cartItems = this.querySelector('cart-drawer-items');
      const cartFooter = this.querySelector('.cart-drawer__footer');
      
      if (cartItems) {
        const newCartItems = drawerHtml.querySelector('cart-drawer-items');
        if (newCartItems) cartItems.replaceWith(newCartItems);
      }
      
      if (cartFooter) {
        const newCartFooter = drawerHtml.querySelector('.cart-drawer__footer');
        if (newCartFooter) cartFooter.replaceWith(newCartFooter);
      }
      
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const bubbleContent = bubbleHtml.querySelector('.shopify-section');
        if (bubbleContent) cartIconBubble.innerHTML = bubbleContent.innerHTML;
      }
      
      this.initExistingReBuyButtons();
      
      if (typeof window.PUB_SUB_EVENTS !== 'undefined' && typeof window.publish !== 'undefined') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'rebuy-refresh' });
      }
    })
    .catch(error => {
      console.error('Error refreshing cart:', error);
    });
  }

  disconnectedCallback() {
    if (this.rebuyObserver) {
      this.rebuyObserver.disconnect();
      this.rebuyObserver = null;
    }
    
    if (this.rebuyRefreshTimeout) {
      clearTimeout(this.rebuyRefreshTimeout);
    }
    
    const monitoredButtons = document.querySelectorAll('[data-rebuy-monitored="true"]');
    monitoredButtons.forEach(button => {
      button.removeEventListener('click', this.handleRebuyButtonClick.bind(this));
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    
    this.initReBuyButtonMonitoring();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      
      this.initExistingReBuyButtons();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
